// Catalog.ID Client — Bitcash Codes, Lock Keys and Release Signatures
// ============================================================================
// The wallet-code grammar is `@catalog-bitcash-code` (libs/bitcash-code,
// docs/cid-governance.md M13; Bitcash Ledger Specification v2 §2), re-exported
// here so the client keeps one import path. What this module adds is what only
// the registration lane needs:
//
//   * `lockKeyFor` with the client's hashes bound (noble SHA-256/SHA-512);
//   * the Ed25519 release signature a lock's condition holder signs (§4.2).
//
// ── a code carries no operator, and no routing prefix any more ──────────────
// Nothing here reads an operator out of a code, and nothing ever may: a code's
// second group is body (§2.4). Since apps/bitcash/docs/plans/bitcash-v2.md M12 this client talks
// to the v2 ledger only, which routes on nothing, so every key it mints is the
// v2 layout (R29) and the generators below take no routing prefix: a
// pilot-layout lock key cannot be minted here.
//
// ── generation ──────────────────────────────────────────────────────────────
// Every key this client mints comes from a byte source through the lib's
// REJECTION sampler — the entropy ceremony's seeded stream (§4.9), the derived
// stream behind `lockKeyFor` (§4.1), or the CSPRNG (`newCode`) — never from a
// modulo of a raw byte, which the pilot generator's own `randomInt` gets wrong.
//
// ── import direction ────────────────────────────────────────────────────────
// `crypto/entropy.ts` must NEVER import this module (registration-restructure
// §13.1's cycle trap): the ceremony receives its wallet composer by injection
// (`drawCeremonyCredentials`'s `mintWalletKey`).

import { sha256, sha512 } from '@noble/hashes/sha2.js';
import { ed25519 } from '@noble/curves/ed25519.js';
import {
    TYPE_CASH,
    compactToDisplay,
    generateCompact,
    generateCompactFrom,
    lockKeyFor as deriveLockKey,
    toCompact,
    verifyCode,
    verifyLockKey,
    verifyReceiveAddress,
    type CodeType,
} from '@catalog-bitcash-code';
import { refForCompact, viewCodeFor, type NetworkId } from '@catalog-bitcash-code/format';
import { encodeUtf8, toHex } from './aes';

export {
    BODY_CHARS as BITCASH_BODY_CHARS,
    COMPACT_LENGTH as BITCASH_COMPACT_LENGTH,
    TYPE_CASH, TYPE_GIFT, TYPE_DEMO, TYPE_FUND, TYPE_AUTH, TYPE_LOCK, TYPE_ACCT,
    type CodeType as WalletType,
    type WalletKind,
    toCompact,
    compactToDisplay,
    displayCode as displayWalletKey,
    verifyWalletKey,
    verifyReceiveAddress,
    verifyLockKey,
    verifyAuthToken,
    walletKind,
    extractType,
} from '@catalog-bitcash-code';

/** Mint a compact key of `type` from `nextByte` through the rejection sampler, in the v2 layout. */
export function generateWalletKeyFrom(nextByte: () => number, type: CodeType): string {
    return generateCompactFrom(nextByte, null, type);
}

/**
 * A fresh code of `type` from the CSPRNG, in display form — a receive address or
 * a token this client creates on the ledger, which never returns its code (T2):
 * the caller writes it down BEFORE the call that creates it.
 */
export function newCode(type: CodeType): string {
    return compactToDisplay(type, generateCompact(null, type));
}

/**
 * The lock key a holder of `publicKey` must go to for lock slot `sequence`
 * (§4.1): derived, never stored, in the v2 layout. `sequence` is 0 for the
 * account lock, 1 for an organization's founder lock, 2 and up for parking locks.
 *
 * @param publicKey 32 raw bytes — the Ed25519 public half derived from the code
 */
export function lockKeyFor(publicKey: Uint8Array, sequence: number): string {
    return deriveLockKey(publicKey, sequence, null, { sha256, sha512 });
}

/** The VIEW code of `code` on `networkId` (§4.21.3, D36): it reads the wallet and moves nothing. */
export function viewCodeOf(code: string, networkId: NetworkId): string {
    return viewCodeFor(toCompact(code), networkId, sha256);
}

/** The ref of `code` on `networkId` (§4.21.3, D2): the wallet's public, one-way name. */
export function walletRefOf(code: string, networkId: NetworkId): string {
    return refForCompact(toCompact(code), networkId, sha256);
}

// ── release conditions and signatures (§4.2) ────────────────────────────────

/** One entry of a lock's condition list. `notAfter` is unix MILLISECONDS. */
export interface LockCondition {
    alg: 'ed25519';
    /** 64 lowercase hex characters — the raw 32-byte public key. */
    publicKey: string;
    /** Absent ⇒ the condition never dies by itself. */
    notAfter?: number;
}

/** Where a release sends the bits (§4.2). Keys may be given in either form. */
export type LockReleaseTarget =
    /** Create and fund a fresh wallet under this CASH key. */
    | { kind: 'cash'; key: string }
    /** Pay an existing wallet through its FUND receive address. */
    | { kind: 'fund'; key: string }
    /** Relock under a new LOCK key and these conditions (§4.4's personalize). */
    | { kind: 'lock'; key: string; conditions: readonly LockCondition[] };

/** Domain separator of the release signature. */
const LOCK_RELEASE_DOMAIN = 'bitcash lock release v1';

const HEX64 = /^[0-9a-f]{64}$/;

/** A lock carries between one and four conditions, fixed at creation (§4.2). */
const MIN_CONDITIONS = 1;
const MAX_CONDITIONS = 4;

/**
 * The conditions array as the EXACT bytes both sides hash (§4.2): no whitespace,
 * each object `{"alg":"ed25519","notAfter":<integer>,"publicKey":"<hex>"}` with
 * `notAfter` omitted when absent.
 *
 * Written out by hand rather than through a canonical-JSON serializer — the
 * plan spells the string out precisely so that neither the Worker nor a C#
 * mirror needs one. `JSON.stringify` of an object literal in this key order
 * produces the same bytes, and the golden vector pins that it does.
 *
 * Throws on anything the Worker would refuse, so a malformed condition can
 * never reach a signature.
 */
export function conditionsJson(conditions: readonly LockCondition[]): string {
    if (!Array.isArray(conditions)
        || conditions.length < MIN_CONDITIONS || conditions.length > MAX_CONDITIONS) {
        throw new Error(`A lock carries ${MIN_CONDITIONS} to ${MAX_CONDITIONS} conditions, `
            + `got ${Array.isArray(conditions) ? conditions.length : 'none'}`);
    }
    const parts = conditions.map(c => {
        if (!c || c.alg !== 'ed25519') {
            throw new Error('A lock condition\'s only algorithm is \'ed25519\'');
        }
        if (typeof c.publicKey !== 'string' || !HEX64.test(c.publicKey)) {
            throw new Error('A lock condition\'s public key is 64 LOWERCASE hex characters');
        }
        if (c.notAfter === undefined) {
            return `{"alg":"ed25519","publicKey":"${c.publicKey}"}`;
        }
        if (!Number.isInteger(c.notAfter) || c.notAfter < 0) {
            throw new Error('A lock condition\'s notAfter is an integer of unix milliseconds');
        }
        return `{"alg":"ed25519","notAfter":${c.notAfter},"publicKey":"${c.publicKey}"}`;
    });
    return `[${parts.join(',')}]`;
}

/** The target descriptor of §4.2 — the string the Worker rebuilds from the
 *  request's `target`, so client and Worker cannot disagree on what was signed. */
function targetDescriptor(target: LockReleaseTarget): string {
    const compact = toCompact(target.key);
    if (target.kind === 'cash') {
        if (!verifyCode(target.key, t => t === TYPE_CASH)) {
            throw new Error('A cash release target must be a valid CASH key');
        }
        return `cash:${compact}`;
    }
    if (target.kind === 'fund') {
        if (!verifyReceiveAddress(target.key)) {
            throw new Error('A fund release target must be a valid receive address');
        }
        return `fund:${compact}`;
    }
    if (target.kind === 'lock') {
        if (!verifyLockKey(target.key)) {
            throw new Error('A relock target must be a valid LOCK key');
        }
        const hash = toHex(sha256(encodeUtf8(conditionsJson(target.conditions))));
        return `lock:${compact}:${hash}`;
    }
    throw new Error(`Unknown release target kind: ${String((target as { kind: string }).kind)}`);
}

/**
 * The bytes a release signature covers (§4.2), Ed25519 over them raw with no
 * hashing in between:
 *
 *     UTF-8("bitcash lock release v1") ‖ 0x0A ‖ UTF-8(lock key, compact)
 *                                      ‖ 0x0A ‖ UTF-8(target descriptor)
 *
 * There is no nonce: a release drains its lock and closes it, so a replay meets
 * a closed lock, and the target is inside the signed bytes, so a captured
 * release cannot be redirected.
 */
export function lockReleaseBytes(lockKey: string, target: LockReleaseTarget): Uint8Array {
    if (!verifyLockKey(lockKey)) throw new Error('A release is signed against a valid LOCK key');
    return encodeUtf8(`${LOCK_RELEASE_DOMAIN}\n${toCompact(lockKey)}\n${targetDescriptor(target)}`);
}

/**
 * Sign {@link lockReleaseBytes} with the 32-byte Ed25519 seed derived from the
 * code that opens this lock (`deriveVoucherSecrets`/`deriveInvitationSecrets`).
 * Ed25519 is deterministic, so the §4.15 signatures are reproducible byte for
 * byte on every implementation — which is how the Worker's test and this one
 * check each other.
 */
export function signLockRelease(
    seed: Uint8Array, lockKey: string, target: LockReleaseTarget,
): Uint8Array {
    if (!(seed instanceof Uint8Array) || seed.length !== 32) {
        throw new Error('A release is signed with a 32-byte Ed25519 seed');
    }
    return ed25519.sign(lockReleaseBytes(lockKey, target), seed);
}
